import { useEffect, useRef, useState } from 'react';

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Mono:wght@400;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #F9F7F4; color: #1a1a1a; font-family: 'Space Mono', monospace; overflow-x: hidden; }

  /* ── NAV ── */
  .ab-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 999;
    background: #1B2FE8; border-bottom: 4px solid #1a1a1a;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 40px; height: 64px;
  }
  .ab-nav-logo { font-family: 'Bebas Neue', monospace; font-size: 28px; letter-spacing: 0.1em; color: #FFD600; text-decoration: none; }
  .ab-nav-links { display: flex; gap: 28px; align-items: center; }
  .ab-nav-link { font-family: 'Space Mono', monospace; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(255,255,255,0.7); text-decoration: none; transition: color 0.2s; }
  .ab-nav-link:hover { color: #FFD600; }
  .ab-nav-link.active { color: #FFD600; }
  .ab-nav-cta { font-family: 'Bebas Neue', monospace; font-size: 16px; letter-spacing: 0.12em; background: #FFD600; color: #1a1a1a; border: 3px solid #1a1a1a; border-radius: 999px; padding: 8px 24px; text-decoration: none; box-shadow: 3px 3px 0 #1a1a1a; transition: transform 0.15s, box-shadow 0.15s; }
  .ab-nav-cta:hover { transform: translateY(-2px); box-shadow: 5px 5px 0 #1a1a1a; }

  /* ── WAVE ── */
  .ab-wave { display: block; width: 100%; overflow: hidden; line-height: 0; margin-top: -3px; margin-bottom: -1px; }
  .ab-wave svg { display: block; width: 100%; }

  /* ══════════════════════════════════════════════════════
     HERO — warm golden cream, light, beautiful
  ══════════════════════════════════════════════════════ */
  .ab-hero {
    background: #FFF8EE;
    padding: 130px 40px 110px;
    position: relative; overflow: hidden; min-height: 600px;
    display: flex; align-items: center;
    border-bottom: 1px solid #EDE8DE;
  }

  /* subtle warm blue dot matrix */
  .ab-hero::before {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(circle, rgba(27,47,232,0.07) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
  }

  /* warm yellow right glow */
  .ab-hero::after {
    content: '';
    position: absolute; top: 0; right: 0; bottom: 0; width: 60%;
    background: radial-gradient(ellipse 70% 80% at 85% 45%, rgba(255,214,0,0.14) 0%, transparent 65%);
    pointer-events: none;
  }

  .ab-hero-inner {
    max-width: 1100px; margin: 0 auto; width: 100%;
    display: flex; align-items: center; justify-content: space-between;
    gap: 60px; flex-wrap: wrap; position: relative; z-index: 2;
  }
  .ab-hero-left { flex: 1; min-width: 280px; }

  .ab-hero-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    border: 1px solid rgba(27,47,232,0.2); border-radius: 2px;
    font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700;
    letter-spacing: 0.25em; color: #1B2FE8; padding: 6px 16px; margin-bottom: 28px;
    text-transform: uppercase; background: rgba(27,47,232,0.04);
  }

  .ab-hero-title {
    font-family: 'Bebas Neue', monospace;
    font-size: clamp(64px, 9vw, 116px); line-height: 0.90;
    letter-spacing: 0.01em; color: #1a1a1a;
  }
  .ab-hero-title em { color: #1B2FE8; font-style: normal; display: block; }

  /* ── Cycling word ── */
  .ab-cycle { position:relative; display:inline-block; color:#1B2FE8; font-style:normal; }
  .ab-cycle-spacer { visibility:hidden; display:block; }
  .ab-cycle-word { position:absolute; top:0; left:0; opacity:0; white-space:nowrap;
    animation: ab-cycle-anim 10s linear infinite; }
  .ab-cw-0{animation-delay:0s} .ab-cw-1{animation-delay:2s} .ab-cw-2{animation-delay:4s}
  .ab-cw-3{animation-delay:6s} .ab-cw-4{animation-delay:8s}
  @keyframes ab-cycle-anim {
    0%  {opacity:0;transform:translateY(14px)}
    6%  {opacity:1;transform:translateY(0)}
    18% {opacity:1;transform:translateY(0)}
    24% {opacity:0;transform:translateY(-14px)}
    100%{opacity:0}
  }

  .ab-hero-desc {
    font-family: 'Space Mono', monospace; font-size: 12px; line-height: 2;
    color: #888; max-width: 440px; margin-top: 28px; letter-spacing: 0.06em;
  }

  /* ── HERO DARK CARD (replaces orbit) ── */
  .ab-hero-card {
    position: relative; flex-shrink: 0;
    width: 340px; min-height: 380px;
    background: linear-gradient(145deg, #0D0D14 0%, #0E1856 55%, #1B2FE8 100%);
    border-radius: 4px; overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,0.05);
  }

  /* grid overlay */
  .ab-hc-grid {
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(27,47,232,0.2) 1px, transparent 1px),
      linear-gradient(90deg, rgba(27,47,232,0.2) 1px, transparent 1px);
    background-size: 32px 32px; pointer-events: none;
  }

  /* sweeping light wave */
  .ab-hc-sweep {
    position: absolute; top: 0; bottom: 0; left: -60%; width: 60%;
    background: linear-gradient(90deg, transparent, rgba(255,214,0,0.05) 40%, rgba(255,255,255,0.04) 55%, transparent);
    animation: ab-hc-pass 6s linear infinite;
    pointer-events: none;
  }
  @keyframes ab-hc-pass { from{left:-60%} to{left:160%} }

  /* ghost text behind */
  .ab-hc-ghost {
    position: absolute; bottom: -10px; right: -10px;
    font-family: 'Bebas Neue', monospace; font-size: 110px;
    letter-spacing: 0.03em; color: rgba(255,214,0,0.05);
    line-height: 1; user-select: none; pointer-events: none;
  }

  /* inner content */
  .ab-hc-inner {
    position: relative; z-index: 2; padding: 32px 28px; height: 100%;
    display: flex; flex-direction: column; justify-content: space-between;
  }

  /* top badge */
  .ab-hc-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,214,0,0.1); border: 1px solid rgba(255,214,0,0.3);
    border-radius: 3px; padding: 5px 12px;
    font-family: 'Space Mono', monospace; font-size: 8px; font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase; color: #FFD600;
  }
  .ab-hc-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #FFD600;
    animation: ab-hc-blink 1.6s ease-in-out infinite;
  }
  @keyframes ab-hc-blink { 0%,100%{opacity:1} 50%{opacity:0.25} }

  /* big glowing number */
  .ab-hc-stat {
    margin: 20px 0 8px;
    font-family: 'Bebas Neue', monospace;
    font-size: clamp(72px, 10vw, 96px); line-height: 0.9;
    letter-spacing: 0.03em; color: #FFD600;
    text-shadow:
      0 0 18px rgba(255,214,0,0.9),
      0 0 50px rgba(255,214,0,0.55),
      0 0 90px rgba(255,214,0,0.25);
    animation: ab-hc-glow 3s ease-in-out infinite;
  }
  @keyframes ab-hc-glow {
    0%,100%{ text-shadow:0 0 18px rgba(255,214,0,0.9),0 0 50px rgba(255,214,0,0.55),0 0 90px rgba(255,214,0,0.25); }
    50%    { text-shadow:0 0 28px rgba(255,214,0,1),  0 0 80px rgba(255,214,0,0.8), 0 0 140px rgba(255,214,0,0.4); }
  }
  .ab-hc-stat-lbl {
    font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700;
    letter-spacing: 0.22em; text-transform: uppercase; color: rgba(255,255,255,0.45);
  }

  /* metric pills row */
  .ab-hc-metrics { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
  .ab-hc-metric {
    display: flex; align-items: center; justify-content: space-between;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 4px; padding: 10px 14px;
  }
  .ab-hc-metric-name {
    font-family: 'Space Mono', monospace; font-size: 8px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,255,255,0.45);
  }
  .ab-hc-metric-val {
    font-family: 'Bebas Neue', monospace; font-size: 18px;
    letter-spacing: 0.06em; line-height: 1;
  }
  .ab-hc-mv-1 { color: #FFD600; }
  .ab-hc-mv-2 { color: #6C8EFF; }
  .ab-hc-mv-3 { color: #4ade80; }

  /* progress bar inside metric */
  .ab-hc-bar-track {
    width: 60px; height: 3px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden;
  }
  .ab-hc-bar-fill {
    height: 100%; border-radius: 2px;
    animation: ab-hc-bar-in 1.5s ease forwards;
  }
  @keyframes ab-hc-bar-in { from{width:0} }

  /* ── MARQUEE ── */
  .ab-marquee-bar {
    background: #0E0E10; padding: 14px 0; overflow: hidden; white-space: nowrap;
    border-top: 1px solid rgba(255,214,0,0.12); border-bottom: 1px solid rgba(255,214,0,0.12);
  }
  .ab-marquee-track { display: inline-flex; animation: ab-marquee 22s linear infinite; }
  .ab-marquee-item {
    font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700;
    letter-spacing: 0.22em; color: rgba(255,255,255,0.4); padding: 0 32px;
    display: flex; align-items: center; gap: 16px; text-transform: uppercase;
    transition: color 0.2s, text-shadow 0.2s; cursor: default;
  }
  .ab-marquee-item:hover { color: #FFD600; text-shadow: 0 0 10px rgba(255,214,0,0.9), 0 0 24px rgba(255,214,0,0.5); }
  .ab-marquee-dot { display: inline-block; width: 4px; height: 4px; background: #FFD600; border-radius: 50%; flex-shrink: 0; }
  @keyframes ab-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }

  /* ── SECTION LABELS ── */
  .ab-eyebrow {
    font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700;
    letter-spacing: 0.3em; text-transform: uppercase; color: #999; margin-bottom: 16px;
  }
  .ab-section-title {
    font-family: 'Bebas Neue', monospace; font-size: clamp(40px,5vw,68px);
    letter-spacing: 0.02em; color: #1a1a1a; line-height: 1; margin-bottom: 56px;
  }
  .ab-section-title em { color: #1B2FE8; font-style: normal; }
  .ab-section-title-light { color: #FFFFFF; }
  .ab-section-title-light em { color: #FFD600; }

  /* ── VALUES / MVPs ── */
  .ab-mvp-section { background: #F9F7F4; padding: 60px 40px; }
  .ab-mvp-inner { max-width: 1100px; margin: 0 auto; }
  .ab-mvp-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: #E8E4DC; border: 1px solid #E8E4DC; }
  .ab-mvp-card { background: #F9F7F4; padding: 44px 32px; position: relative; transition: background 0.2s; cursor: default; }
  .ab-mvp-card:hover { background: #fff; }
  .ab-mvp-card-band { height: 3px; width: 48px; margin-bottom: 32px; }
  .ab-mvp-title { font-family: 'Bebas Neue', monospace; font-size: 28px; letter-spacing: 0.04em; color: #1a1a1a; margin-bottom: 14px; }
  .ab-mvp-text { font-family: 'Space Mono', monospace; font-size: 10px; line-height: 2; color: #888; }
  .ab-mvp-label {
    display: inline-block; margin-top: 24px;
    font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase; color: #999;
    border: 1px solid #E8E4DC; border-radius: 2px; padding: 4px 10px;
  }

  /* ── STATS ── */
  .ab-stats-section {
    background: linear-gradient(160deg, #1C1C20 0%, #16161A 100%);
    padding: 60px 40px;
    border-top: 1px solid rgba(255,214,0,0.1);
    border-bottom: 1px solid rgba(255,214,0,0.1);
    position: relative; overflow: hidden;
  }
  .ab-stats-section::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(27,47,232,0.06) 0%, transparent 70%);
    pointer-events: none;
  }
  .ab-stats-inner {
    max-width: 1100px; margin: 0 auto; position: relative; z-index: 1;
    display: grid; grid-template-columns: repeat(4,1fr); gap: 1px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.06);
  }
  .ab-stat-card { padding: 48px 28px; text-align: center; background: transparent; }
  .ab-stat-num { font-family: 'Bebas Neue', monospace; font-size: 64px; letter-spacing: 0.02em; line-height: 1; color: #FFD600; margin-bottom: 8px; }
  .ab-stat-label { font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(255,255,255,0.35); }

  /* ── STORY ── */
  .ab-story-section { background: #F9F7F4; padding: 60px 40px; border-top: 1px solid #E8E4DC; }
  .ab-story-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
  .ab-story-left { position: relative; }
  .ab-story-img-wrap { position: relative; display: inline-block; width: 100%; }
  .ab-story-img {
    width: 100%; aspect-ratio: 4/5;
    background: linear-gradient(160deg, #1C1C20 0%, #16161A 100%);
    border: 1px solid #E8E4DC; border-radius: 4px;
    display: flex; align-items: center; justify-content: center;
    position: relative; overflow: hidden;
  }
  .ab-story-img::after {
    content: ''; position: absolute; inset: 0;
    background-image: linear-gradient(rgba(255,214,0,0.04) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,214,0,0.04) 1px, transparent 1px);
    background-size: 40px 40px;
  }
  .ab-story-img-inner { position: relative; z-index: 1; text-align: center; padding: 40px; }
  .ab-story-big-text { font-family: 'Bebas Neue', monospace; font-size: clamp(56px,7vw,96px); line-height: 1; letter-spacing: 0.06em; color: #FFD600; display: flex; flex-direction: column; align-items: center; gap: 4px; }
  .ab-story-big-text .abt-line1 { font-size: clamp(36px,4.5vw,60px); letter-spacing: 0.28em; color: rgba(255,214,0,0.45); }
  .ab-story-big-text .abt-line2 { font-size: clamp(64px,8.5vw,110px); letter-spacing: 0.04em; color: #FFD600; line-height: 0.9; }
  .ab-story-big-text .abt-line3 { font-size: clamp(36px,4.5vw,60px); letter-spacing: 0.28em; color: rgba(255,214,0,0.45); }
  .ab-story-small-text { font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.3); margin-top: 16px; }
  .ab-story-year { position: absolute; top: -16px; left: -16px; background: #F9F7F4; border: 1px solid #E8E4DC; padding: 12px 18px; z-index: 3; box-shadow: 4px 4px 0 #1a1a1a; }
  .ab-story-year-num { font-family: 'Bebas Neue', monospace; font-size: 28px; letter-spacing: 0.04em; color: #1a1a1a; line-height: 1; }
  .ab-story-year-label { font-family: 'Space Mono', monospace; font-size: 8px; font-weight: 700; letter-spacing: 0.15em; color: #999; text-transform: uppercase; }
  .ab-story-sticker { position: absolute; bottom: -16px; right: -16px; background: #1C1C20; border: 1px solid rgba(255,214,0,0.3); padding: 12px 18px; z-index: 3; box-shadow: 4px 4px 0 #1a1a1a; animation: ab-float 5s ease-in-out infinite; }
  .ab-story-sticker-text { font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #FFD600; line-height: 1.6; }
  @keyframes ab-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  .ab-story-text { font-family: 'Space Mono', monospace; font-size: 11px; line-height: 2; color: #888; margin-bottom: 36px; }
  .ab-checklist { display: flex; flex-direction: column; gap: 12px; margin-bottom: 36px; }
  .ab-check-item { display: flex; align-items: center; gap: 14px; font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; color: #555; }
  .ab-check-dot { width: 4px; height: 4px; background: #1B2FE8; border-radius: 50%; flex-shrink: 0; }
  .ab-story-btn { font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; background: #1C1C20; color: #fff; border: none; border-radius: 2px; padding: 14px 28px; cursor: pointer; text-decoration: none; display: inline-block; transition: background 0.2s; }
  .ab-story-btn:hover { background: #1B2FE8; }

  /* ── SCROLLING STRIPS ── */
  .ab-strips { overflow: hidden; }
  .ab-strip { padding: 16px 0; overflow: hidden; white-space: nowrap; border-top: 1px solid #E8E4DC; }
  .ab-strip:last-child { border-bottom: 1px solid #E8E4DC; }
  .ab-strip-track { display: inline-flex; animation: ab-strip 16s linear infinite; }
  .ab-strip:nth-child(even) .ab-strip-track { animation-direction: reverse; }
  .ab-strip-item { font-family: 'Bebas Neue', monospace; font-size: 28px; letter-spacing: 0.1em; padding: 0 24px; }
  .ab-strip-1 { background: #F9F7F4; } .ab-strip-1 .ab-strip-item { color: #1a1a1a; }
  .ab-strip-2 { background: #1C1C20; border-top: none; } .ab-strip-2 .ab-strip-item { color: rgba(255,255,255,0.5); }
  .ab-strip-3 { background: #F9F7F4; border-top: none; } .ab-strip-3 .ab-strip-item { color: #1B2FE8; }
  @keyframes ab-strip { from{transform:translateX(0)} to{transform:translateX(-50%)} }

  /* ── TEAM ── */
  .ab-team-section {
    background: linear-gradient(160deg, #1C1C20 0%, #16161A 60%, #1A1620 100%);
    padding: 60px 40px; border-top: 1px solid rgba(255,214,0,0.1); position: relative; overflow: hidden;
  }
  .ab-team-section::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 50% 60% at 80% 40%, rgba(255,214,0,0.04) 0%, transparent 70%); pointer-events: none; }
  .ab-team-inner { max-width: 1100px; margin: 0 auto; position: relative; z-index: 1; }
  .ab-team-sub { font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.2em; color: rgba(255,255,255,0.3); text-transform: uppercase; margin-bottom: 36px; }
  .ab-team-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.06); }
  .ab-team-card { background: #1C1C20; padding: 0; position: relative; overflow: hidden; transition: background 0.2s; cursor: default; }
  .ab-team-card:hover { background: rgba(255,255,255,0.03); }
  .ab-team-photo { height: 200px; display: flex; align-items: center; justify-content: center; position: relative; border-bottom: 1px solid rgba(255,255,255,0.08); overflow: hidden; }
  .ab-team-photo-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 32px 32px; }
  .ab-team-body { padding: 28px; }
  .ab-team-name { font-family: 'Bebas Neue', monospace; font-size: 26px; letter-spacing: 0.04em; color: #FFFFFF; margin-bottom: 4px; }
  .ab-team-role { font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: #FFD600; margin-bottom: 16px; }
  .ab-team-bio { font-family: 'Space Mono', monospace; font-size: 10px; line-height: 1.9; color: rgba(255,255,255,0.4); margin-bottom: 20px; }
  .ab-team-tags { display: flex; flex-wrap: wrap; gap: 8px; }
  .ab-team-tag { font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: 1px solid rgba(255,255,255,0.1); padding: 5px 12px; color: rgba(255,255,255,0.3); transition: color 0.15s, border-color 0.15s; cursor: default; }
  .ab-team-tag:hover { color: #FFD600; border-color: rgba(255,214,0,0.3); }

  /* ── CTA ── */
  .ab-cta { background: #F9F7F4; padding: 120px 40px; border-top: 1px solid #E8E4DC; position: relative; }
  .ab-cta-inner { max-width: 820px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
  .ab-cta-eyebrow { font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; color: #999; margin-bottom: 16px; }
  .ab-cta-title { font-family: 'Bebas Neue', monospace; font-size: clamp(44px,6vw,72px); line-height: 0.95; color: #1a1a1a; margin-bottom: 20px; }
  .ab-cta-title span { color: #1B2FE8; }
  .ab-cta-text { font-family: 'Space Mono', monospace; font-size: 11px; line-height: 2; color: #888; }
  .ab-cta-right { display: flex; flex-direction: column; gap: 16px; }
  .ab-cta-btn { font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; background: #1C1C20; color: #fff; border: none; border-radius: 2px; padding: 16px 28px; cursor: pointer; text-decoration: none; display: inline-block; transition: background 0.2s; text-align: center; }
  .ab-cta-btn:hover { background: #1B2FE8; }
  .ab-cta-btn-ghost { font-family: 'Space Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; background: transparent; color: #1a1a1a; border: 1px solid #E8E4DC; border-radius: 2px; padding: 16px 28px; cursor: pointer; text-decoration: none; display: inline-block; transition: border-color 0.2s, color 0.2s; text-align: center; }
  .ab-cta-btn-ghost:hover { border-color: #1a1a1a; color: #1a1a1a; }

  /* ── FOOTER — identical to Courses/Blog/Contact ── */
  .ab-footer { background: #EDEAE2; padding: 48px 40px 36px; border-top: 1px solid #D8D4CC; }
  .ab-footer-inner { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 24px; }
  .ab-footer-brand { font-family: 'Bebas Neue', monospace; font-size: 24px; letter-spacing: 0.12em; color: #1a1a1a; }
  .ab-footer-links { display: flex; gap: 28px; }
  .ab-footer-link { font-family: 'Space Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #888; text-decoration: none; transition: color 0.2s; }
  .ab-footer-link:hover { color: #1a1a1a; }
  .ab-footer-copy { font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: 0.12em; color: #aaa; text-transform: uppercase; }

  /* ── REVEAL ── */
  .ab-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
  .ab-reveal.visible { opacity: 1; transform: translateY(0); }
  .ab-reveal-d1 { transition-delay: 0.08s; }
  .ab-reveal-d2 { transition-delay: 0.16s; }
  .ab-reveal-d3 { transition-delay: 0.24s; }

  /* ── RESPONSIVE ── */
  @media (max-width: 960px) {
    .ab-mvp-grid { grid-template-columns: 1fr; }
    .ab-stats-inner { grid-template-columns: repeat(2,1fr); }
    .ab-story-inner { grid-template-columns: 1fr; gap: 48px; }
    .ab-team-grid { grid-template-columns: 1fr; }
    .ab-hero-inner { flex-direction: column; align-items: flex-start; }
    .ab-cta-inner { grid-template-columns: 1fr; gap: 48px; }
    .ab-hero-card { width: 280px; }
  }
  @media (max-width: 600px) {
    .ab-nav { padding: 0 20px; }
    .ab-nav-links { display: none; }
    .ab-hero { padding: 110px 20px 60px; }
    .ab-mvp-section, .ab-story-section, .ab-team-section, .ab-cta { padding-left: 20px; padding-right: 20px; }
    .ab-stats-inner { grid-template-columns: repeat(2,1fr); }
    .ab-hero-card { width: 100%; }
  }
`;

function injectStyle(id, css) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id; el.textContent = css;
  document.head.appendChild(el);
}

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.ab-reveal');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const Wave = ({ top, bottom }) => (
  <div className="ab-wave" style={{ background: bottom }}>
    <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: 60 }}>
      <path d="M0,30 C360,60 1080,0 1440,30 L1440,0 L0,0 Z" fill={top} />
    </svg>
  </div>
);

/* ── SVG Avatars ── */
const AvatarArjun = () => (
  <svg width="140" height="160" viewBox="0 0 140 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* body / shirt */}
    <ellipse cx="70" cy="175" rx="52" ry="38" fill="#1B2FE8"/>
    <rect x="30" y="148" width="80" height="30" rx="8" fill="#1B2FE8"/>
    {/* collar */}
    <polygon points="70,148 58,148 65,162" fill="#fff" opacity="0.2"/>
    <polygon points="70,148 82,148 75,162" fill="#fff" opacity="0.2"/>
    {/* neck */}
    <rect x="60" y="118" width="20" height="18" rx="4" fill="#C68642"/>
    {/* head */}
    <ellipse cx="70" cy="100" rx="36" ry="38" fill="#C68642"/>
    {/* hair */}
    <ellipse cx="70" cy="66" rx="36" ry="16" fill="#1a1a1a"/>
    <rect x="34" y="66" width="10" height="22" rx="5" fill="#1a1a1a"/>
    <rect x="96" y="66" width="10" height="22" rx="5" fill="#1a1a1a"/>
    {/* ears */}
    <ellipse cx="34" cy="102" rx="6" ry="8" fill="#B5763A"/>
    <ellipse cx="106" cy="102" rx="6" ry="8" fill="#B5763A"/>
    {/* eyes */}
    <ellipse cx="56" cy="100" rx="7" ry="7.5" fill="#fff"/>
    <ellipse cx="84" cy="100" rx="7" ry="7.5" fill="#fff"/>
    <circle cx="57" cy="101" r="4.5" fill="#2C1810"/>
    <circle cx="85" cy="101" r="4.5" fill="#2C1810"/>
    <circle cx="58.5" cy="99.5" r="1.5" fill="#fff"/>
    <circle cx="86.5" cy="99.5" r="1.5" fill="#fff"/>
    {/* eyebrows */}
    <rect x="49" y="91" width="14" height="3" rx="1.5" fill="#1a1a1a"/>
    <rect x="77" y="91" width="14" height="3" rx="1.5" fill="#1a1a1a"/>
    {/* nose */}
    <ellipse cx="70" cy="113" rx="4" ry="3" fill="#B5763A"/>
    {/* smile */}
    <path d="M58 122 Q70 132 82 122" stroke="#8B5E3C" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    {/* subtle stubble */}
    <ellipse cx="70" cy="126" rx="14" ry="5" fill="#B5763A" opacity="0.4"/>
    {/* FFD600 accent pin on shirt */}
    <circle cx="70" cy="155" r="5" fill="#FFD600"/>
  </svg>
);

const AvatarPriya = () => (
  <svg width="140" height="160" viewBox="0 0 140 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* body / top */}
    <ellipse cx="70" cy="175" rx="52" ry="38" fill="#D4145A"/>
    <rect x="30" y="148" width="80" height="30" rx="8" fill="#D4145A"/>
    {/* neck */}
    <rect x="61" y="118" width="18" height="18" rx="4" fill="#D4955A"/>
    {/* head */}
    <ellipse cx="70" cy="100" rx="34" ry="37" fill="#D4955A"/>
    {/* long hair - back */}
    <ellipse cx="70" cy="68" rx="35" ry="14" fill="#1a1a1a"/>
    <rect x="100" y="68" width="12" height="80" rx="6" fill="#1a1a1a"/>
    <rect x="28" y="68" width="12" height="70" rx="6" fill="#1a1a1a"/>
    {/* hair top */}
    <ellipse cx="70" cy="64" rx="35" ry="15" fill="#1a1a1a"/>
    <path d="M35 64 Q70 44 105 64" fill="#1a1a1a"/>
    {/* ears */}
    <ellipse cx="36" cy="101" rx="5" ry="7" fill="#C07840"/>
    <ellipse cx="104" cy="101" rx="5" ry="7" fill="#C07840"/>
    {/* earrings */}
    <circle cx="36" cy="110" r="3" fill="#FFD600"/>
    <circle cx="104" cy="110" r="3" fill="#FFD600"/>
    {/* eyes */}
    <ellipse cx="57" cy="100" rx="7" ry="7" fill="#fff"/>
    <ellipse cx="83" cy="100" rx="7" ry="7" fill="#fff"/>
    <circle cx="58" cy="101" r="4.5" fill="#2C1810"/>
    <circle cx="84" cy="101" r="4.5" fill="#2C1810"/>
    <circle cx="59.5" cy="99.5" r="1.5" fill="#fff"/>
    <circle cx="85.5" cy="99.5" r="1.5" fill="#fff"/>
    {/* eyelashes */}
    <path d="M50 95 Q57 91 64 95" stroke="#1a1a1a" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <path d="M76 95 Q83 91 90 95" stroke="#1a1a1a" strokeWidth="2" fill="none" strokeLinecap="round"/>
    {/* eyebrows - arched */}
    <path d="M49 90 Q57 85 65 90" stroke="#1a1a1a" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    <path d="M75 90 Q83 85 91 90" stroke="#1a1a1a" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    {/* nose */}
    <ellipse cx="70" cy="112" rx="3.5" ry="2.5" fill="#C07840"/>
    {/* lips */}
    <path d="M61 121 Q70 128 79 121" stroke="#C04060" strokeWidth="2.5" fill="#E06080" strokeLinecap="round"/>
    <path d="M61 121 Q70 118 79 121" fill="#E06080"/>
    {/* bindi */}
    <circle cx="70" cy="80" r="3" fill="#D4145A"/>
  </svg>
);

const AvatarVikram = () => (
  <svg width="140" height="160" viewBox="0 0 140 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* body / shirt */}
    <ellipse cx="70" cy="175" rx="52" ry="38" fill="#1a1a1a"/>
    <rect x="30" y="148" width="80" height="30" rx="8" fill="#1a1a1a"/>
    {/* collar & tie hint */}
    <polygon points="70,148 60,148 66,160" fill="#fff" opacity="0.15"/>
    <polygon points="70,148 80,148 74,160" fill="#fff" opacity="0.15"/>
    <rect x="67" y="148" width="6" height="14" rx="2" fill="#FFD600" opacity="0.7"/>
    {/* neck */}
    <rect x="60" y="118" width="20" height="18" rx="4" fill="#A0693A"/>
    {/* head */}
    <ellipse cx="70" cy="100" rx="35" ry="37" fill="#A0693A"/>
    {/* hair - slightly wavy, medium length */}
    <ellipse cx="70" cy="65" rx="35" ry="15" fill="#2A1A0A"/>
    <rect x="35" y="65" width="10" height="28" rx="5" fill="#2A1A0A"/>
    <rect x="95" y="65" width="10" height="24" rx="5" fill="#2A1A0A"/>
    <path d="M35 68 Q70 52 105 68" fill="#2A1A0A"/>
    {/* ears */}
    <ellipse cx="35" cy="102" rx="6" ry="8" fill="#8C5A2A"/>
    <ellipse cx="105" cy="102" rx="6" ry="8" fill="#8C5A2A"/>
    {/* eyes */}
    <ellipse cx="57" cy="100" rx="7" ry="7" fill="#fff"/>
    <ellipse cx="83" cy="100" rx="7" ry="7" fill="#fff"/>
    <circle cx="58" cy="101" r="4.5" fill="#1a0800"/>
    <circle cx="84" cy="101" r="4.5" fill="#1a0800"/>
    <circle cx="59.5" cy="99.5" r="1.5" fill="#fff"/>
    <circle cx="85.5" cy="99.5" r="1.5" fill="#fff"/>
    {/* eyebrows - thicker */}
    <rect x="48" y="90" width="18" height="3.5" rx="1.75" fill="#2A1A0A"/>
    <rect x="74" y="90" width="18" height="3.5" rx="1.75" fill="#2A1A0A"/>
    {/* nose */}
    <ellipse cx="70" cy="113" rx="4.5" ry="3.5" fill="#8C5A2A"/>
    {/* beard / stubble */}
    <ellipse cx="70" cy="128" rx="22" ry="12" fill="#2A1A0A" opacity="0.55"/>
    <path d="M48 118 Q70 138 92 118" fill="#2A1A0A" opacity="0.45"/>
    {/* mouth */}
    <path d="M60 122 Q70 129 80 122" stroke="#7A4A1A" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* mustache */}
    <path d="M58 118 Q70 122 82 118" stroke="#2A1A0A" strokeWidth="3" strokeLinecap="round" fill="none"/>
    {/* glasses */}
    <rect x="46" y="95" width="20" height="14" rx="5" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none"/>
    <rect x="74" y="95" width="20" height="14" rx="5" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none"/>
    <line x1="66" y1="102" x2="74" y2="102" stroke="rgba(255,255,255,0.35)" strokeWidth="2"/>
    <line x1="44" y1="102" x2="46" y2="102" stroke="rgba(255,255,255,0.35)" strokeWidth="2"/>
    <line x1="94" y1="102" x2="97" y2="102" stroke="rgba(255,255,255,0.35)" strokeWidth="2"/>
  </svg>
);

const AVATARS = [AvatarArjun, AvatarPriya, AvatarVikram];

function TeamCard({ member, delay, avatarIndex }) {
  const Avatar = AVATARS[avatarIndex];
  return (
    <div className={`ab-team-card ab-reveal ab-reveal-d${delay}`}>
      <div className="ab-team-photo" style={{ background: member.bg }}>
        <div className="ab-team-photo-grid" />
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'flex-end', justifyContent:'center', height:'100%' }}>
          <Avatar />
        </div>
      </div>
      <div className="ab-team-body">
        <div className="ab-team-name">{member.name}</div>
        <div className="ab-team-role">{member.role}</div>
        <p className="ab-team-bio">{member.bio}</p>
        <div className="ab-team-tags">
          {member.tags.map((t, i) => <span key={i} className="ab-team-tag">{t}</span>)}
        </div>
      </div>
    </div>
  );
}

/* ─── DATA ─── */
const MARQUEE_ITEMS = [
  'About Us','Digital Marketing','SEO & Growth','Paid Ads','Social Media','Content Strategy',
  'Our Story','Meet the Team','Analytics','Email Marketing','About Us','Digital Marketing',
  'SEO & Growth','Paid Ads','Social Media','Content Strategy','Our Story','Meet the Team',
];

const MVPs = [
  { title:'Our Mission', text:'To help brands cut through the digital noise with strategies that are data-driven, creative, and built for measurable growth — not just impressions and likes.', label:'Why we exist', band:'#1a1a1a' },
  { title:'Our Vision',  text:'A world where every business, from a local startup to a national brand, has access to digital marketing that actually moves the needle. We are making that accessible and accountable.', label:"Where we're going", band:'#1B2FE8' },
  { title:'Our Promise', text:'No vanity metrics. No long lock-in contracts. Just transparent strategy, honest reporting, and campaigns that deliver real revenue impact for every client we work with.', label:'What we guarantee', band:'#FFD600' },
];

const STATS = [
  { num:'180+', label:'Brands Grown'          },
  { num:'3.8×', label:'Avg. ROAS Delivered'   },
  { num:'94%',  label:'Client Retention Rate' },
  { num:'8+',   label:'Years in the Industry' },
];

const CHECKLIST = [
  'Strategy built on real data, not guesswork',
  'Dedicated account manager for every client',
  'Full-funnel campaigns — awareness to conversion',
  'Monthly performance reports with clear KPIs',
  'Flexible plans — no long lock-in contracts',
  'Specialists across SEO, Paid Ads, Social & Content',
];

const TEAM = [
  { emoji:'🎯', name:'Arjun Sharma', role:'Head of Growth & SEO',      bio:'8 years driving organic growth for D2C and SaaS brands. Managed SEO campaigns generating over 12M monthly impressions across competitive verticals.',      tags:['Technical SEO','Content Strategy','GA4'],  bg:'linear-gradient(160deg,#1C1C20,#16161A)' },
  { emoji:'📊', name:'Priya Reddy',  role:'Paid Media Lead',            bio:'Ex-Meta and Google Ads specialist with ₹10Cr+ in managed ad spend. Obsessed with creative testing, audience segmentation and efficient CPAs.',             tags:['Meta Ads','Google Ads','Retargeting'],      bg:'linear-gradient(160deg,#16161A,#1A1620)' },
  { emoji:'✍️', name:'Vikram Nair',  role:'Content & Social Strategist', bio:'Formerly at a leading content agency, Vikram has built content engines for 50+ brands — from editorial calendars to viral social campaigns.',              tags:['Content Marketing','Social Media','Video'], bg:'linear-gradient(160deg,#1C1C20,#1a1a1a)' },
];

const STRIPS = [
  { items:['SEO ·','GROWTH ·','STRATEGY ·','RESULTS ·','SEO ·','GROWTH ·','STRATEGY ·','RESULTS ·'], cls:'ab-strip-1' },
  { items:['PAID ADS ◆','CONTENT ◆','SOCIAL ◆','ANALYTICS ◆','PAID ADS ◆','CONTENT ◆','SOCIAL ◆','ANALYTICS ◆'], cls:'ab-strip-2' },
  { items:['BRAND ·','DIGITAL ·','MARKETING ·','REVENUE ·','BRAND ·','DIGITAL ·','MARKETING ·','REVENUE ·'], cls:'ab-strip-3' },
];

export default function About() {
  useEffect(() => { injectStyle('ab-styles', STYLE); }, []);
  useReveal();

  return (
    <div>

      {/* HERO — warm golden cream */}
      <section className="ab-hero">
        <div className="ab-hero-inner">
          <div className="ab-hero-left ab-reveal">
            <div className="ab-hero-eyebrow">About Us</div>
            <h1 className="ab-hero-title">
              WE GROW<br />
              <span className="ab-cycle">
                <span className="ab-cycle-spacer">BUSINESSES</span>
                {['BRANDS','REVENUE','TRAFFIC','LEADS','BUSINESSES'].map((w,i) => (
                  <span key={i} className={`ab-cycle-word ab-cw-${i}`}>{w}</span>
                ))}
              </span>
              <br />DIGITALLY.
            </h1>
            <p className="ab-hero-desc">
              We are a full-service digital marketing agency helping businesses build visibility,
              generate leads and drive real revenue — through strategy that is data-led and
              creatively uncompromising.
            </p>
          </div>

          {/* Dark glowing hero card */}
          <div className="ab-hero-card ab-reveal ab-reveal-d1">
            <div className="ab-hc-grid" />
            <div className="ab-hc-sweep" />
            <div className="ab-hc-ghost">DA</div>
            <div className="ab-hc-inner">
              <div className="ab-hc-badge">
                <span className="ab-hc-badge-dot" /> Est. 2017 · Active
              </div>
              <div>
                <div className="ab-hc-stat">180+</div>
                <div className="ab-hc-stat-lbl">Brands Grown</div>
              </div>
              <div className="ab-hc-metrics">
                <div className="ab-hc-metric">
                  <span className="ab-hc-metric-name">Avg ROI on Ad Spend</span>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="ab-hc-bar-track"><div className="ab-hc-bar-fill" style={{width:'80%',background:'#FFD600'}} /></div>
                    <span className="ab-hc-metric-val ab-hc-mv-1">3.8×</span>
                  </div>
                </div>
                <div className="ab-hc-metric">
                  <span className="ab-hc-metric-name">Client Retention</span>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="ab-hc-bar-track"><div className="ab-hc-bar-fill" style={{width:'94%',background:'#6C8EFF'}} /></div>
                    <span className="ab-hc-metric-val ab-hc-mv-2">94%</span>
                  </div>
                </div>
                <div className="ab-hc-metric">
                  <span className="ab-hc-metric-name">Organic Impressions</span>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="ab-hc-bar-track"><div className="ab-hc-bar-fill" style={{width:'90%',background:'#4ade80'}} /></div>
                    <span className="ab-hc-metric-val ab-hc-mv-3">12M+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Wave top="#FFF8EE" bottom="#0E0E10" />

      {/* MARQUEE */}
      <div className="ab-marquee-bar">
        <div className="ab-marquee-track">
          {MARQUEE_ITEMS.map((item, i) => (
            <span key={i} className="ab-marquee-item">{item}<span className="ab-marquee-dot" /></span>
          ))}
        </div>
      </div>

      <Wave top="#0E0E10" bottom="#F9F7F4" />

      {/* VALUES */}
      <section className="ab-mvp-section">
        <div className="ab-mvp-inner">
          <p className="ab-eyebrow ab-reveal">What drives us</p>
          <h2 className="ab-section-title ab-reveal">Core <em>Values</em></h2>
          <div className="ab-mvp-grid">
            {MVPs.map((m, i) => (
              <div className={`ab-mvp-card ab-reveal ab-reveal-d${i+1}`} key={i}>
                <div className="ab-mvp-card-band" style={{ background: m.band }} />
                <h3 className="ab-mvp-title">{m.title}</h3>
                <p className="ab-mvp-text">{m.text}</p>
                <span className="ab-mvp-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Wave top="#F9F7F4" bottom="#1C1C20" />

      {/* STATS */}
      <section className="ab-stats-section">
        <div className="ab-stats-inner">
          {STATS.map((s, i) => (
            <div className={`ab-stat-card ab-reveal ab-reveal-d${i+1}`} key={i}>
              <div className="ab-stat-num">{s.num}</div>
              <div className="ab-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <Wave top="#1C1C20" bottom="#F9F7F4" />

      {/* STORY */}
      <section className="ab-story-section">
        <div className="ab-story-inner">
          <div className="ab-story-left ab-reveal">
            <div className="ab-story-img-wrap">
              <div className="ab-story-img">
                <div className="ab-story-img-inner">
                  <div className="ab-story-big-text">
                    <span className="abt-line1">GROWTH</span>
                    <span className="abt-line2">DIGITAL</span>
                    <span className="abt-line3">AGENCY</span>
                  </div>
                  <div className="ab-story-small-text">Est. 2017 · Growth Agency</div>
                </div>
              </div>
              <div className="ab-story-year">
                <div className="ab-story-year-num">2017</div>
                <div className="ab-story-year-label">Founded</div>
              </div>
              <div className="ab-story-sticker">
                <div className="ab-story-sticker-text">Built by<br />Marketers</div>
              </div>
            </div>
          </div>

          <div>
            <p className="ab-eyebrow ab-reveal">Our journey</p>
            <h2 className="ab-section-title ab-reveal">The Story<br /><em>Behind Us</em></h2>
            <p className="ab-story-text ab-reveal">
              We started in 2017 with a simple belief — that small and mid-sized businesses
              deserve the same quality of digital marketing that big brands get. Three
              performance marketers, one shared office, and a mission to make digital
              growth accountable.
              <br /><br />
              Today we manage campaigns across SEO, paid media, social, content and email
              for over 180 brands. Every strategy we build is tied to real business outcomes —
              not impressions, not followers, but revenue.
            </p>
            <div className="ab-checklist ab-reveal">
              {CHECKLIST.map((item, i) => (
                <div className="ab-check-item" key={i}>
                  <div className="ab-check-dot" />
                  {item}
                </div>
              ))}
            </div>
            <a href="#contact" className="ab-story-btn ab-reveal">Work With Us →</a>
          </div>
        </div>
      </section>

      {/* STRIPS */}
      <div className="ab-strips">
        {STRIPS.map((strip, si) => (
          <div className={`ab-strip ${strip.cls}`} key={si}>
            <div className="ab-strip-track">
              {strip.items.map((s, i) => <span key={i} className="ab-strip-item">{s}</span>)}
            </div>
          </div>
        ))}
      </div>

      <Wave top="#F9F7F4" bottom="#1C1C20" />

      {/* TEAM */}
      <section className="ab-team-section">
        <div className="ab-team-inner">
          <p className="ab-eyebrow ab-reveal" style={{ color:'rgba(255,255,255,0.3)' }}>The people behind it</p>
          <h2 className="ab-section-title ab-section-title-light ab-reveal">Meet the<br /><em>Team</em></h2>
          <p className="ab-team-sub ab-reveal">Strategists. Creatives. Data obsessives.</p>
          <div className="ab-team-grid">
            {TEAM.map((member, i) => <TeamCard key={i} member={member} delay={i+1} avatarIndex={i} />)}
          </div>
        </div>
      </section>



    </div>
  );
}